import { $convertToMarkdownString } from "@lexical/markdown";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import classNames from "classnames";
import type { EditorState } from "lexical";
import type { CSSProperties } from "react";
import { useWatch } from "react-hook-form";
import { fontFamilyCSS, textColorCSS } from "schemas";
import { heightStyles, pointerStyles, positionStyles, textStyles, widthStyles } from "../../styles";
import type { PropsWithOptionalAutoFocus, PropsWithOptionalPlaceholder } from "../../types";
import { BottomToolbarPlugin } from "./BottomToolbarPlugin";
import styles from "./Editor.module.css";
import { SubmitPlugin } from "./SubmitPlugin";
import { TopToolbarPlugin } from "./TopToolbarPlugin";
import { LITESITE_TRANSFORMERS, type RichEditorType } from "./Wysiwyg";

interface Props extends PropsWithOptionalPlaceholder, PropsWithOptionalAutoFocus {
  type: RichEditorType;
  setValue: (value: string) => void;
  onSubmit?: () => Promise<void>;
  fontSelectable: boolean;
  textAlignmentSelectable: boolean;
  fontFamilyName: string;
  textAlignmentName: string;
  textColorName: string;
}

export function Editor({
  type,
  placeholder,
  onSubmit,
  setValue,
  autoFocus,
  fontSelectable,
  textAlignmentSelectable,
  fontFamilyName,
  textAlignmentName,
  textColorName,
}: Props) {
  const fontFamily = useWatch({ name: fontFamilyName });
  const textColor = useWatch({ name: textColorName });
  const textAlignment = useWatch({ name: textAlignmentName });

  const fontStyles = fontFamily ? fontFamilyCSS(fontFamily) : {};
  const textColorStyles = textColorCSS(textColor) as CSSProperties;

  const onChange = (editorState: EditorState) => {
    editorState.read(() => {
      setValue($convertToMarkdownString(LITESITE_TRANSFORMERS));
    });
  };

  return (
    <div className={classNames(positionStyles.relative, heightStyles.full, styles.wrapper)}>
      <RichTextPlugin
        contentEditable={
          <ContentEditable
            style={{ ...textColorStyles, ...fontStyles }}
            className={classNames(
              textStyles.size100,
              textStyles.fontTheme,
              textStyles.preWrap,
              heightStyles.full,
              styles.inline,
              {
                [styles.editor]: type === "form",
                [textStyles.editorBorderSelection]: type !== "form",
                [textStyles.center]: textAlignment === "Center",
              },
            )}
          />
        }
        placeholder={
          <div
            style={fontStyles}
            className={classNames(
              textStyles.size100,
              textStyles.fontPoppins,
              textStyles.lsColor,
              pointerStyles.eventsNone,
              positionStyles.absolute,
              positionStyles.top275,
              positionStyles.left006,
              positionStyles.right006,
              widthStyles.full,
              styles.placeholder,
              {
                [textStyles.neutral300]: type === "form",
                [textStyles.editorBorder]: type !== "form",
                [textStyles.center]: textAlignment === "Center",
              },
            )}
          >
            {placeholder}
          </div>
        }
        ErrorBoundary={LexicalErrorBoundary}
      />
      {autoFocus && <AutoFocusPlugin />}
      {onSubmit && <SubmitPlugin onlyMeta onSubmit={onSubmit} />}
      <MarkdownShortcutPlugin transformers={LITESITE_TRANSFORMERS} />
      <HistoryPlugin />
      <OnChangePlugin onChange={onChange} />
      <ListPlugin />
      <LinkPlugin />
      <TopToolbarPlugin fontSelectable={fontSelectable} fontFamilyName={fontFamilyName} textColorName={textColorName} />
      <BottomToolbarPlugin textAlignmentSelectable={textAlignmentSelectable} textAlignmentName={textAlignmentName} />
    </div>
  );
}
